import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowRight, X, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * Heatmap of last 12 weeks (today inclusive). Cells colored by daily P&L magnitude.
 *
 * Iter 44 — drilldown polish:
 *  • Click a non-empty cell → open a portaled popover anchored to the cell
 *    (escapes the bento-tile transform stacking context the same way
 *    MetricInfoTooltip does), fetches `/api/dashboard/day/{date}` and
 *    shows a compact trade preview right next to the heatmap. No page nav.
 *  • Today's cell gets a subtle ring so the user can orient at a glance.
 *  • "View all" link inside the popover still navigates to the filtered
 *    /trades page for power users who want the full journal view.
 *
 * Receives `dailyMap`: { "YYYY-MM-DD": number } already converted to the
 * active currency.
 */
export default function CalendarHeatmap({ dailyMap = {}, currency = "USD" }) {
  const [openDate, setOpenDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Iter 127 — auto-detect the actual date range of the user's trade
  // data. If the newest trade is older than the default 12-week window
  // ending today (e.g. after importing a historical CSV), shift the
  // window so its right edge = the newest trade's date. Otherwise the
  // user sees an empty grid and no idea their data is elsewhere.
  const dataRange = useMemo(() => {
    const dates = Object.keys(dailyMap).filter((d) => Math.abs(Number(dailyMap[d] || 0)) > 0);
    if (dates.length === 0) return { min: null, max: null };
    dates.sort();
    return { min: dates[0], max: dates[dates.length - 1] };
  }, [dailyMap]);

  const { weeks, maxAbs, monthLabels, todayIso, windowEndIso, shifted } = useMemo(() => {
    const totalWeeks = 12;
    const totalDays = totalWeeks * 7;
    const today = new Date();
    const todayIso = today.toISOString().slice(0, 10);

    // Default end = current Saturday. Iter 127: if the whole trade
    // dataset ends before the start of this default window, shift the
    // window to end on the Saturday immediately after the newest trade.
    let end = new Date(today);
    const dayOfWeek = end.getDay(); // 0=Sun
    end.setDate(end.getDate() + (6 - dayOfWeek));
    let start = new Date(end);
    start.setDate(end.getDate() - (totalDays - 1));

    let shifted = false;
    if (dataRange.max) {
      const startIso = start.toISOString().slice(0, 10);
      if (dataRange.max < startIso) {
        // All trade data is older than the default window — shift.
        const maxD = new Date(dataRange.max + "T00:00:00");
        const mdow = maxD.getDay();
        end = new Date(maxD);
        end.setDate(maxD.getDate() + (6 - mdow));
        start = new Date(end);
        start.setDate(end.getDate() - (totalDays - 1));
        shifted = true;
      }
    }

    const cells = [];
    let max = 0;
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      const v = Number(dailyMap[iso] || 0);
      if (Math.abs(v) > max) max = Math.abs(v);
      cells.push({ date: iso, value: v, day: d });
    }
    const weeksArr = [];
    for (let w = 0; w < totalWeeks; w++) {
      weeksArr.push(cells.slice(w * 7, (w + 1) * 7));
    }

    const labels = [];
    let lastMonth = -1;
    weeksArr.forEach((wk, idx) => {
      const firstDay = wk[0].day;
      if (firstDay.getMonth() !== lastMonth) {
        labels.push({ idx, label: firstDay.toLocaleString(undefined, { month: "short" }) });
        lastMonth = firstDay.getMonth();
      }
    });

    return {
      weeks: weeksArr,
      maxAbs: max,
      monthLabels: labels,
      todayIso,
      windowEndIso: end.toISOString().slice(0, 10),
      shifted,
    };
  }, [dailyMap, dataRange]);

  const colorFor = (v) => {
    if (v === 0) return "bg-[var(--bt-surface-hover)] border-[var(--bt-border)]";
    const intensity = maxAbs > 0 ? Math.min(1, Math.abs(v) / maxAbs) : 0;
    let bucket = 1;
    if (intensity > 0.75) bucket = 4;
    else if (intensity > 0.45) bucket = 3;
    else if (intensity > 0.2) bucket = 2;
    if (v > 0) {
      return [
        "bg-[var(--bt-accent)]/15 border-[var(--bt-accent)]/30",
        "bg-[var(--bt-accent)]/35 border-[var(--bt-accent)]/40",
        "bg-[var(--bt-accent)]/65 border-[var(--bt-accent)]/60",
        "bg-[var(--bt-accent)] border-[var(--bt-accent)]",
      ][bucket - 1];
    }
    return [
      "bg-[var(--bt-danger)]/15 border-[var(--bt-danger)]/30",
      "bg-[var(--bt-danger)]/35 border-[var(--bt-danger)]/40",
      "bg-[var(--bt-danger)]/65 border-[var(--bt-danger)]/60",
      "bg-[var(--bt-danger)] border-[var(--bt-danger)]",
    ][bucket - 1];
  };

  const symbol = currency === "INR" ? "₹" : "$";
  const fmt = (n) =>
    `${n < 0 ? "-" : ""}${symbol}${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const handleCellClick = (e, date, hasTrades, isFuture) => {
    if (isFuture || !hasTrades) return;
    setOpenDate(date);
    setAnchorEl(e.currentTarget);
  };

  const closePopover = () => {
    setOpenDate(null);
    setAnchorEl(null);
  };

  return (
    <div data-testid="calendar-heatmap" className="space-y-3">
      {/* Iter 127 — window-shift banner: when the user's trade data
          all pre-dates the default 12-week window (e.g. after a
          historical CSV import), we auto-anchor the grid on the
          newest trade and tell them clearly instead of showing an
          empty grid. */}
      {shifted && dataRange.min && dataRange.max && (
        <div
          data-testid="heatmap-window-shifted-banner"
          className="rounded-sm border px-3 py-2 text-xs flex items-start gap-2"
          style={{
            borderColor: "color-mix(in srgb, var(--bt-accent) 30%, var(--bt-border))",
            background: "color-mix(in srgb, var(--bt-accent) 5%, var(--bt-surface))",
          }}
        >
          <span aria-hidden="true" style={{ color: "var(--bt-accent)" }}>ℹ</span>
          <span className="text-[var(--bt-text)] leading-snug">
            <span className="font-semibold">Showing your data window</span>{" "}
            <span className="text-[var(--bt-text-muted)]">
              — no trades in the last 12 weeks. Your journal covers{" "}
              <span className="bt-num">{dataRange.min}</span> to{" "}
              <span className="bt-num">{dataRange.max}</span>.
            </span>
          </span>
        </div>
      )}
      {/* ── Mobile list view (<640px) — heatmap cells are ~28px which is
          well below the 44px touch-target minimum. Below sm we render
          the same last-14-days data as a scrollable vertical list with
          finger-friendly rows. Desktop keeps the 12×7 grid. */}
      <div className="sm:hidden space-y-1.5" data-testid="calendar-heatmap-mobile-list">
        <div className="flex items-baseline justify-between mb-1">
          <p className="text-[10px] uppercase tracking-wider text-[var(--bt-text-muted)]">
            Last 14 days
          </p>
          <p className="text-[10px] text-[var(--bt-text-muted)]">Tap to see trades</p>
        </div>
        {(() => {
          // Pull the last 14 cells (2 weeks) from the pre-computed grid,
          // newest → oldest. Rendered as a compact list. Same click
          // handler / popover as desktop.
          const flat = weeks.flat();
          const last14 = flat.slice(-14).reverse();
          return last14.map((cell) => {
            const isFuture = cell.day > new Date();
            const hasTrades = cell.value !== 0;
            const isToday = cell.date === todayIso;
            const dow = cell.day.toLocaleDateString(undefined, { weekday: "short" });
            const md = cell.day.toLocaleDateString(undefined, { day: "numeric", month: "short" });
            const bandColor =
              cell.value > 0 ? "var(--bt-accent)"
              : cell.value < 0 ? "var(--bt-danger)"
              : "var(--bt-border)";
            const alpha =
              maxAbs > 0 ? Math.max(0.18, Math.min(1, Math.abs(cell.value) / maxAbs)) : 0.18;
            return (
              <button
                key={`m-${cell.date}`}
                type="button"
                data-testid={`heatmap-list-row-${cell.date}`}
                disabled={isFuture || !hasTrades}
                onClick={(e) => handleCellClick(e, cell.date, hasTrades, isFuture)}
                className={`w-full min-h-[44px] flex items-center gap-3 px-3 py-2 rounded-sm border text-left transition-colors ${
                  isFuture ? "opacity-40" : ""
                } ${hasTrades ? "hover:bg-[var(--bt-surface-hover)] active:bg-[var(--bt-surface-hover)] cursor-pointer" : "cursor-default"}`}
                style={{
                  borderColor: isToday
                    ? "color-mix(in srgb, var(--bt-text) 40%, var(--bt-border))"
                    : "var(--bt-border)",
                  background: "var(--bt-surface)",
                }}
              >
                {/* Color band showing P&L magnitude */}
                <span
                  aria-hidden="true"
                  className="w-1 self-stretch rounded-sm shrink-0"
                  style={{ background: hasTrades
                    ? `color-mix(in srgb, ${bandColor} ${Math.round(alpha * 100)}%, transparent)`
                    : "transparent" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-tight">
                    {dow} <span className="text-[var(--bt-text-muted)]">·</span> {md}
                    {isToday && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[var(--bt-accent)]">Today</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    {isFuture ? "—" : hasTrades ? "Tap to open" : "No trades"}
                  </div>
                </div>
                <div
                  className={`font-mono text-sm font-semibold whitespace-nowrap ${
                    cell.value > 0 ? "text-[var(--bt-accent)]" :
                    cell.value < 0 ? "text-[var(--bt-danger)]" :
                    "text-[var(--bt-text-muted)]"
                  }`}
                >
                  {hasTrades ? fmt(cell.value) : (isFuture ? "" : "$0")}
                </div>
              </button>
            );
          });
        })()}
      </div>

      {/* Desktop grid (≥640px) */}
      {/* Month labels */}
      <div className="hidden sm:grid grid-cols-[36px_repeat(12,minmax(0,1fr))] gap-1 text-[10px] text-[var(--bt-text-muted)] uppercase tracking-wider">
        <div />
        {weeks.map((wk, idx) => {
          const lbl = monthLabels.find((m) => m.idx === idx);
          const weekKey = wk[0]?.date || `w-${idx}`;
          return <div key={weekKey} className="text-center">{lbl ? lbl.label : ""}</div>;
        })}
      </div>

      {/* Day rows (desktop only) */}
      <div className="hidden sm:grid grid-cols-[36px_repeat(12,minmax(0,1fr))] gap-1">
        {dayLabels.map((dl, rowIdx) => (
          <React.Fragment key={dl}>
            <div className="text-[10px] text-[var(--bt-text-muted)] uppercase tracking-wider flex items-center">
              {rowIdx % 2 === 1 ? dl : ""}
            </div>
            {weeks.map((wk) => {
              const cell = wk[rowIdx];
              const cls = colorFor(cell.value);
              const isFuture = cell.day > new Date();
              const hasTrades = cell.value !== 0;
              const clickable = !isFuture && hasTrades;
              const isToday = cell.date === todayIso;
              const isOpen = openDate === cell.date;
              return (
                <div
                  key={cell.date}
                  data-testid={`heatmap-cell-${cell.date}`}
                  title={`${cell.date}: ${fmt(cell.value)}${clickable ? " (click for details)" : ""}`}
                  onClick={(e) => handleCellClick(e, cell.date, hasTrades, isFuture)}
                  className={`aspect-square w-full rounded-sm border transition-all duration-150 ${
                    isFuture ? "bg-transparent border-[var(--bt-border)]/50" : cls
                  } ${clickable ? "cursor-pointer hover:ring-2 hover:ring-white/40 hover:scale-110" : ""}
                  ${isToday ? "ring-2 ring-[var(--bt-text)]/50 ring-offset-1 ring-offset-[var(--bt-surface)]" : ""}
                  ${isOpen ? "ring-2 ring-[var(--bt-accent)]" : ""}`}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Legend (desktop only) */}
      <div className="hidden sm:flex items-center justify-end gap-2 text-[10px] text-[var(--bt-text-muted)]">
        <span>Loss</span>
        <div className="w-3 h-3 bg-[var(--bt-danger)] rounded-sm" />
        <div className="w-3 h-3 bg-[var(--bt-danger)]/65 rounded-sm" />
        <div className="w-3 h-3 bg-[var(--bt-danger)]/35 rounded-sm" />
        <div className="w-3 h-3 bg-[var(--bt-surface-hover)] border border-[var(--bt-border)] rounded-sm" />
        <div className="w-3 h-3 bg-[var(--bt-accent)]/35 rounded-sm" />
        <div className="w-3 h-3 bg-[var(--bt-accent)]/65 rounded-sm" />
        <div className="w-3 h-3 bg-[var(--bt-accent)] rounded-sm" />
        <span>Profit</span>
      </div>

      {openDate && anchorEl && (
        <DayDrilldownPopover
          date={openDate}
          anchorEl={anchorEl}
          currency={currency}
          onClose={closePopover}
        />
      )}
    </div>
  );
}

// ── Drilldown popover (Iter 44) ───────────────────────────────────────────
function DayDrilldownPopover({ date, anchorEl, currency, onClose }) {
  const [state, setState] = useState("loading"); // "loading" | "done" | "error"
  const [data, setData] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);
  const nav = useNavigate();

  // Fetch the day's trades
  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setData(null);
    (async () => {
      try {
        const { data: resp } = await api.get(`/dashboard/day/${date}`);
        if (!cancelled) {
          setData(resp);
          setState("done");
        }
      } catch (e) {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date]);

  // Position relative to the anchor cell, clamped into the viewport.
  const updatePosition = () => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const W = 320;
    const PAD = 12;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const popupH = popupRef.current?.offsetHeight || 240;
    let left = r.left + r.width / 2 - W / 2;
    if (left + W + PAD > vw) left = vw - W - PAD;
    if (left < PAD) left = PAD;
    // Prefer below the cell; flip above if no room.
    let top;
    if (r.bottom + popupH + PAD < vh) {
      top = r.bottom + 8;
    } else if (r.top - popupH - PAD > 0) {
      top = r.top - popupH - 8;
    } else {
      top = Math.max(PAD, vh - popupH - PAD);
    }
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    updatePosition();
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl, state]);

  useEffect(() => {
    const handler = () => updatePosition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  // Outside-click + Escape dismiss
  useEffect(() => {
    const onDown = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) {
        onClose();
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorEl, onClose]);

  const sym = currency === "INR" ? "₹" : "$";
  const fmt = (n) =>
    `${n < 0 ? "-" : ""}${sym}${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtTradeValue = (t) => {
    const v = currency === "INR" ? t.net_pnl_inr : t.net_pnl_usd;
    return fmt(v);
  };

  // Pretty date — "Tue 4 Feb"
  const prettyDate = useMemo(() => {
    try {
      const d = new Date(`${date}T12:00:00Z`);
      return d.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return date;
    }
  }, [date]);

  return createPortal(
    <div
      ref={popupRef}
      role="dialog"
      data-testid="heatmap-day-popover"
      className="rounded-md border shadow-xl"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: 320,
        maxWidth: "calc(100vw - 16px)",
        maxHeight: "calc(100vh - 24px)",
        background: "var(--bt-surface-2)",
        borderColor: "var(--bt-border)",
        color: "var(--bt-text)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--bt-border)" }}>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[var(--bt-text-muted)]">Day detail</p>
          <p className="font-heading text-base" data-testid="heatmap-day-popover-date">{prettyDate}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="heatmap-day-popover-close"
          className="rounded-md p-1 hover:bg-[var(--bt-surface)] transition-colors"
        >
          <X className="w-4 h-4" style={{ color: "var(--bt-text-muted)" }} />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-3 overflow-y-auto" style={{ minHeight: 80 }}>
        {state === "loading" && (
          <div className="flex items-center justify-center py-6 gap-2 text-sm text-[var(--bt-text-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {state === "error" && (
          <p className="text-sm text-[var(--bt-loss)]">Couldn't load this day's trades.</p>
        )}
        {state === "done" && data && (
          <>
            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <Stat
                label="Trades"
                value={data.trade_count}
                testId="heatmap-day-popover-count"
              />
              <Stat
                label="Win / Loss"
                value={`${data.wins}W / ${data.losses}L`}
                testId="heatmap-day-popover-wl"
              />
              <Stat
                label={`Net (${currency})`}
                value={fmt(currency === "INR" ? data.net_pnl_inr : data.net_pnl_usd)}
                accent={
                  (currency === "INR" ? data.net_pnl_inr : data.net_pnl_usd) >= 0
                    ? "var(--bt-win)"
                    : "var(--bt-loss)"
                }
                testId="heatmap-day-popover-pnl"
              />
            </div>
            <ul className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="heatmap-day-popover-trades">
              {data.trades.slice(0, 8).map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-2 text-xs border rounded-sm px-2 py-1.5"
                  style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface)" }}
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-mono font-semibold">{t.symbol}</span>
                    <span
                      className="ml-1.5 text-[10px] uppercase"
                      style={{ color: t.side === "buy" ? "var(--bt-win)" : "var(--bt-loss)" }}
                    >
                      {t.side === "buy" ? "long" : "short"}
                    </span>
                    <span className="ml-1.5 text-[10px] text-[var(--bt-text-muted)] truncate">
                      {t.broker}
                    </span>
                  </div>
                  <span
                    className="font-mono"
                    style={{ color: (t.net_pnl || 0) >= 0 ? "var(--bt-win)" : "var(--bt-loss)" }}
                  >
                    {fmtTradeValue(t)}
                  </span>
                </li>
              ))}
              {data.trades.length > 8 && (
                <li className="text-[10px] text-[var(--bt-text-muted)] text-center pt-1">
                  +{data.trades.length - 8} more
                </li>
              )}
            </ul>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t flex justify-end" style={{ borderColor: "var(--bt-border)" }}>
        <button
          type="button"
          data-testid="heatmap-day-popover-view-all"
          onClick={() => {
            nav(`/trades?start_date=${date}&end_date=${date}`);
            onClose();
          }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-1.5 transition-colors"
          style={{ background: "var(--bt-accent)", color: "#000" }}
        >
          View in journal <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>,
    document.body
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-sm border px-2 py-1.5"
      style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface)" }}
    >
      <p className="text-[9px] uppercase tracking-wider text-[var(--bt-text-muted)]">{label}</p>
      <p className="font-mono text-sm mt-0.5" style={{ color: accent || "var(--bt-text)" }}>
        {value}
      </p>
    </div>
  );
}
